import { useMemo, useEffect } from 'react';
import { useApplicationContext } from '../context/ApplicationContext';
import { navigationConfig } from '../config/navigationConfig';
import { NavItem } from '../config/navigationConfig.types';
import {
  buildResourceAbilityIndex,
  canSeeResourceMenuItem,
  ResourceAbilityIndex,
} from '../security/resourceAbilityVisibility';

/**
 * Recursively filter the navigation tree by abilities.
 * - Items with abilityKey are checked against the index; if denied/missing → pruned.
 * - Items WITHOUT abilityKey are visible by default (no key from business = always show).
 * - Branch nodes are pruned if they have zero surviving children.
 * - alwaysVisible items bypass all checks.
 */
function filterNavTree(
  items: NavItem[],
  abilityIndex: ResourceAbilityIndex
): NavItem[] {
  return items.reduce<NavItem[]>((acc, item) => {
    if (item.alwaysVisible) {
      if (item.children) {
        acc.push({ ...item, children: filterNavTree(item.children, abilityIndex) });
      } else {
        acc.push(item);
      }
      return acc;
    }

    // If item has an abilityKey, it must pass the RBAC check
    if (item.abilityKey) {
      if (!canSeeResourceMenuItem(abilityIndex, item.abilityKey)) {
        return acc; // PRUNED
      }
    }
    // No abilityKey = visible by default (business hasn't gated this item)

    if (item.children) {
      const filteredChildren = filterNavTree(item.children, abilityIndex);
      if (filteredChildren.length > 0) {
        acc.push({ ...item, children: filteredChildren });
      }
      return acc;
    }

    acc.push(item);
    return acc;
  }, []);
}

/**
 * Hook: consumes raw config + abilities → produces filtered NavItem[]
 * Stores result in ApplicationContext for global access.
 */
export function useNavigationFilter() {
  const { abilities, tenantId, setFilteredNavigation, setFilteredVerticalSections, setMenuVisibility } = useApplicationContext();

  const filteredNav = useMemo(() => {
    if (!abilities || abilities.length === 0 || !tenantId) {
      return navigationConfig.filter(item => item.alwaysVisible);
    }

    const module = tenantId.split('_')[0] || '';
    const index = buildResourceAbilityIndex(abilities, module);
    return filterNavTree(navigationConfig, index);
  }, [abilities, tenantId]);

  useEffect(() => {
    // Store the full filtered navigation array in context
    setFilteredNavigation(filteredNav);
    
    // Also store as sections for backward compatibility
    const sections: Record<string, any> = {};
    filteredNav.forEach(item => {
      if (item.id !== 'home') {
        sections[item.id] = item;
      }
    });
    setFilteredVerticalSections(sections);

    // Automatically derive all top-level section IDs from navigationConfig (excluding 'home')
    // This ensures visibility tracking stays in sync when sections are added/removed
    const allSectionIds = navigationConfig
      .map(item => item.id)
      .filter(id => id !== 'home');
    
    allSectionIds.forEach(id => {
      const visible = filteredNav.some(item => item.id === id);
      setMenuVisibility(id, visible);
    });
  }, [filteredNav, setFilteredNavigation, setFilteredVerticalSections, setMenuVisibility]);

  return filteredNav;
}
